import React, { useEffect, useState } from 'react'
import { List, ListItem, ListItemText, Typography } from '@mui/material'
import { makeStyles, withStyles } from '@mui/styles'
import { CustomCheckbox } from '../../components/restyled-mui/CustomInputs'
import { modules } from '../../utils/moduleConfig'

export const CustomList = withStyles(_theme => ({
  root: {
    padding: 0,
  },
}))(List)

export const CustomListItemText = withStyles(_theme => ({
  root: {},
}))(ListItemText)

const useStyles = makeStyles(theme => ({
  listContainer: {
    border: `1px solid ${theme.palette.table.cellBorder}`,
    marginTop: 10,
    marginBottom: 20,
    minHeight: 0,
    maxHeight: 500,
    // overflowY: 'auto',
  },

  zebraItem: {
    borderBottom: `1px solid ${theme.palette.table.cellBorder}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.table.cellBg,
    },
  },

  listItemRoot: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  listItemText: {
    '& .MuiListItemText-primary': {
      fontSize: 14,
    },
    flex: '0 0 140px',
  },

  descriptionText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    flex: 1,
  },

  selectAllItem: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    fontWeight: 600,
  },
}))

// Turn a permission name into a readable label:
// "Conwx_portfolio" -> "Conwx Portfolio", "Overview" -> "Overview"
const formatPermissionName = name =>
  String(name)
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const PermissionsSelection = props => {
  const classes = useStyles(props)
  const { allPermissions, user, setUser, setFormState } = props
  const [syncPermissions, setSyncModulePermissions] = useState([])
  const userPermissions = user.permissions

  useEffect(() => {
    const syncLists = allPermissions.map(item => ({
      name: item,
      checked: userPermissions.includes(item.toLowerCase()),
    }))
    setSyncModulePermissions(syncLists)
  }, [allPermissions])

  const toggleAllPermissions = e => {
    const isChecked = e.target.checked

    const newSyncList = syncPermissions.map(permission => ({
      ...permission,
      checked: isChecked,
    }))

    setSyncModulePermissions(newSyncList)

    if (isChecked) {
      const modulePermissionAll = newSyncList.map(permission =>
        permission.name.toLowerCase(),
      )
      setUser(prev => ({ ...prev, permissions: modulePermissionAll }))
      setFormState(prev => ({
        ...prev,
        permissions: modulePermissionAll,
        permissionsError: '',
      }))
    } else {
      setUser(prev => ({ ...prev, permissions: [] }))
      setFormState(prev => ({
        ...prev,
        permissions: [],
        permissionsError: 'User must have at least one permission',
      }))
    }
  }

  const handleCheckboxClick = index => {
    const currentItem = syncPermissions[index]
    currentItem.checked = !currentItem.checked
    setSyncModulePermissions([
      ...syncPermissions.slice(0, index),
      currentItem,
      ...syncPermissions.slice(index + 1),
    ])
    const updatedModulePermissions = syncPermissions
      .filter(item => item.checked)
      .map(item => item.name.toLowerCase())

    setUser(prev => ({ ...prev, permissions: updatedModulePermissions }))
    setFormState(prev => ({
      ...prev,
      permissions: updatedModulePermissions,
      permissionsError:
        updatedModulePermissions.length === 0
          ? 'User must have at least one permission'
          : '',
    }))
  }

  const allChecked =
    syncPermissions.length > 0 && syncPermissions.every(p => p.checked)

  const indeterminate =
    userPermissions.length > 0 && syncPermissions.some(p => !p.checked)

  return (
    <div className={classes.listContainer}>
      <CustomList disablePadding dense>
        {/* Select All row */}
        <ListItem
          className={`${classes.zebraItem} ${classes.listItemRoot}  ${classes.selectAllItem}`}
          secondaryAction={
            <CustomCheckbox
              indeterminate={indeterminate}
              checked={allChecked}
              onChange={toggleAllPermissions}
            />
          }
        >
          <CustomListItemText
            primary="Select All"
            className={classes.listItemText}
          />
        </ListItem>

        {/* Permission rows */}
        {syncPermissions.map((item, index) => {
          const moduleKey = item.name.toLowerCase()
          const description = modules[moduleKey]?.description || ''

          return (
            <ListItem
              key={item.name}
              className={`${classes.zebraItem} ${classes.listItemRoot}`}
              secondaryAction={
                <CustomCheckbox
                  checked={item.checked}
                  onChange={() => handleCheckboxClick(index)}
                />
              }
            >
              <CustomListItemText
                primary={formatPermissionName(item.name)}
                className={classes.listItemText}
              />
              <Typography className={classes.descriptionText}>
                {description}
              </Typography>
            </ListItem>
          )
        })}
      </CustomList>
    </div>
  )
}

export default PermissionsSelection
